/**
 * Ref-master category configuration.
 *
 * This is the single source of truth that maps form field IDs to their
 * ref_master category keys.  All dynamic category construction (e.g.
 * TRUCK_MODEL_VOLVO) happens here so the rest of the codebase stays
 * category-agnostic.
 *
 * Rules:
 *  - A resolver returns a category string when enough context is known.
 *  - It returns null when a required parent value is still unset / placeholder,
 *    meaning the dropdown should remain in its placeholder state.
 */

import { REF_KEYS } from './refKeys'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface CategoryDeps {
  make?: string
  engineMfg?: string
  transMfg?: string
  /** Truck/Tractor body type — value of the 'use' field in standard-equipment */
  bodyUse?: string
  /** Trailer body type — value of the 'type' field in standard-equipment */
  bodyType?: string
  /** Truck/Tractor Subtype — value of the 'useSubtype' field, when the Use has Subtypes */
  bodyUseSubtype?: string
  /** Trailer Subtype — value of the 'typeSubtype' field, when the Type has Subtypes */
  bodyTypeSubtype?: string
}

/** A function that resolves the API category for a field given its deps. */
export type CategoryResolver = (deps: CategoryDeps) => string | null

// ── Helpers ───────────────────────────────────────────────────────────────────

const PLACEHOLDER = new Set(['', 'Select'])

/**
 * Normalise a user-facing label to an uppercase category-key suffix.
 * e.g. "Volvo"          → "VOLVO"
 *      "Detroit Diesel" → "DETROIT_DIESEL"
 *      "PACCAR/DAF"     → "PACCAR_DAF"
 */
export function normaliseToKey(label: string): string {
  return label
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

function isValid(value: string | undefined): boolean {
  return !!value && !PLACEHOLDER.has(value)
}

// ── Basic Information overrides ───────────────────────────────────────────────

/**
 * Basic-information fields that should be augmented from the API even though
 * they already carry static fallback options in sections.data.ts.
 */
export const BASIC_INFO_API_FIELDS: Record<string, string> = {
  typeOfLoss:  REF_KEYS.TYPE_OF_LOSS,
  vehicleType: REF_KEYS.VEHICLE_TYPE,
  contactState: REF_KEYS.COUNTRY_STATE
}

// ── Truck / Tractor ───────────────────────────────────────────────────────────

/** Standard Equipment categories for Truck/Tractor. */
export const TRUCK_STANDARD_CATEGORIES: Record<string, CategoryResolver> = {
  use:             () => REF_KEYS.TRUCK_USE,
  useSubtype:      ({ bodyUse }) => isValid(bodyUse) ? `TRUCK_USE_SUBTYPE_${normaliseToKey(bodyUse!)}` : null,
  make:            () => REF_KEYS.TRUCK_MAKE,
  model:           ({ make }) => isValid(make) ? `TRUCK_MODEL_${normaliseToKey(make!)}` : null,
  engineMfg:       () => REF_KEYS.TRUCK_ENGINE_MFG,
  engineModel:     ({ engineMfg }) => isValid(engineMfg) ? `TRUCK_ENGINE_MODEL_${normaliseToKey(engineMfg!)}` : null,
  engineHP:        () => REF_KEYS.TRUCK_ENGINE_HP,
  transMfg:        () => REF_KEYS.TRUCK_TRANS_MFG,
  frontAxleRating: () => REF_KEYS.TRUCK_FRONT_AXLE_RATING,
  rearAxleRating:  () => REF_KEYS.TRUCK_REAR_AXLE_RATING,
  gvw:             () => REF_KEYS.TRUCK_GVWR,
  style:           () => REF_KEYS.TRUCK_STYLE,
  truck_type:      () => REF_KEYS.TRUCK_TYPE,
  noOfSpeeds:      ({ transMfg }) => isValid(transMfg) ? `TRUCK_TRANS_SPEED_${normaliseToKey(transMfg!)}` : null
}

/** Additional Options categories for Truck/Tractor. */
export const TRUCK_ADDITIONAL_CATEGORIES: Record<string, CategoryResolver> = {
  convSleeperSize:  () => REF_KEYS.TRUCK_SLEEPER_SIZE,
  coeSize:          () => REF_KEYS.TRUCK_COE_SIZE,
  apu:              () => REF_KEYS.TRUCK_APU,
  trimLevel:        () => REF_KEYS.TRUCK_TRIM_LEVEL,
  frontWheelType:   () => REF_KEYS.TRUCK_WHEEL_TYPE,
  osRearWheelType:  () => REF_KEYS.TRUCK_WHEEL_TYPE,
  isRearWheelType:  () => REF_KEYS.TRUCK_WHEEL_TYPE,
  frontTireSize:    () => REF_KEYS.TRUCK_TIRE_SIZE,  // shared with rearTireSize
  rearTireSize:     () => REF_KEYS.TRUCK_TIRE_SIZE,  // shared with frontTireSize
  suspension:       () => REF_KEYS.TRUCK_SUSPENSION,
  liftGateCapacity: () => REF_KEYS.TRUCK_LIFT_GATE_CAPACITY,
  fifthWheelType:   () => REF_KEYS.TRUCk_FIFTH_WHEEL_TYPE,
  hitchType:        () => REF_KEYS.TRUCK_HITCH_TYPE
}

/** Vehicle Condition categories for Truck/Tractor. */
export const TRUCK_CONDITIONAL_CATEGORIES: Record<string, CategoryResolver> = {
  interior: () => REF_KEYS.TRUCK_VEHICLE_CONDITON,
  body: () => REF_KEYS.TRUCK_VEHICLE_CONDITON,
  interiorGlass: () => REF_KEYS.TRUCK_VEHICLE_CONDITON,
  interiorPaint: () => REF_KEYS.TRUCK_VEHICLE_CONDITON,
  drivetrainEngine: () => REF_KEYS.TRUCK_VEHICLE_CONDITON,
  drivetrainTransmission: () => REF_KEYS.TRUCK_VEHICLE_CONDITON,
}

// ── Trailer ───────────────────────────────────────────────────────────────────

/** Standard Equipment categories for Trailer. */
export const TRAILER_STANDARD_CATEGORIES: Record<string, CategoryResolver> = {
  make:   () => REF_KEYS.TRAILER_MAKE,
  model:  ({ make }) => isValid(make) ? `TRAILER_MODEL_${normaliseToKey(make!)}` : null,
  type:   () => REF_KEYS.TRAILER_TYPE,
  typeSubtype: ({ bodyType }) => isValid(bodyType) ? `TRAILER_TYPE_SUBTYPE_${normaliseToKey(bodyType!)}` : null,
  exterior: () => REF_KEYS.TRAILER_EXTERIOR,
  length: () => REF_KEYS.TRAILER_LENGTH,
}

/** Additional Options categories for Trailer. */
export const TRAILER_ADDITIONAL_CATEGORIES: Record<string, CategoryResolver> = {
  height:         () => REF_KEYS.TRAILER_HEIGHT,
  width:          () => REF_KEYS.TRAILER_WIDTH,
  suspension:     () => REF_KEYS.TRAILER_SUSPENSION,
  gawrGvv:        () => REF_KEYS.TRAILER_GAWR,
  interiorLining: () => REF_KEYS.TRAILER_INTERIOR_LINING,
  rearDoorType:   () => REF_KEYS.TRAILER_REAR_DOOR_TYPE,
  osWheelType:    () => REF_KEYS.TRAILER_OS_WHEEL_TYPE,
  isWheelType:    () => REF_KEYS.TRAILER_IS_WHEEL_TYPE,
  tireSize:       () => REF_KEYS.TRAILER_TIRE_SIZE,
}

/** Vehicle Condition categories for Trailer. */
export const TRAILER_CONDITIONAL_CATEGORIES: Record<string, CategoryResolver> = {
  interiorLining: () => REF_KEYS.TRAILER_VEHICLE_CONDITON,
  exteriorBody: () => REF_KEYS.TRAILER_VEHICLE_CONDITON,
  exteriorPaint: () => REF_KEYS.TRAILER_VEHICLE_CONDITON,
  mechanicalHydraulics: () => REF_KEYS.TRAILER_VEHICLE_CONDITON,
  mechanicalCoolingUnit: () => REF_KEYS.TRAILER_VEHICLE_CONDITON
}

// ── Section lookup maps ───────────────────────────────────────────────────────

type SectionCategoryMap = {
  truck?: Record<string, CategoryResolver>
  trailer?: Record<string, CategoryResolver>
  shared?: Record<string, CategoryResolver>
}

const SECTION_CATEGORY_MAPS: Record<string, SectionCategoryMap> = {
  'standard-equipment': {
    truck:   TRUCK_STANDARD_CATEGORIES,
    trailer: TRAILER_STANDARD_CATEGORIES,
  },
  'additional-options': {
    truck:   TRUCK_ADDITIONAL_CATEGORIES,
    trailer: TRAILER_ADDITIONAL_CATEGORIES,
  },
  'vehicle-condition': {
    truck: TRUCK_CONDITIONAL_CATEGORIES,
    trailer: TRAILER_CONDITIONAL_CATEGORIES,
  }
  // 'body-information' is intentionally absent — its ref categories are
  // resolved dynamically in resolveFieldCategory below, not via this map.
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Returns all non-dependent (base) category keys for a section + vehicleType.
 * Used for the batch pre-fetch that fires when a section opens.
 *
 * "Non-dependent" means the resolver returns a value even with empty deps.
 */
export function getBaseCategoriesForSection(
  sectionId: string,
  vehicleType: string
): string[] {
  const map = SECTION_CATEGORY_MAPS[sectionId]
  if (!map) return []

  const resolvers =
    vehicleType === 'Truck/Tractor'
      ? { ...(map.truck ?? {}), ...(map.shared ?? {}) }
      : vehicleType === 'Trailer'
      ? { ...(map.trailer ?? {}), ...(map.shared ?? {}) }
      : map.shared ?? {}

  const categories = new Set<string>()
  for (const resolver of Object.values(resolvers)) {
    const cat = resolver({})  // no deps — only base (non-dependent) ones resolve
    if (cat) categories.add(cat)
  }
  return Array.from(categories)
}

/**
 * Resolves the category key for a specific field given its current dep values.
 * Returns null when a required parent value is missing / placeholder.
 */
export function resolveFieldCategory(
  fieldId: string,
  sectionId: string,
  vehicleType: string,
  deps: CategoryDeps
): string | null {
  // Basic-info override
  if (sectionId === 'basic-information') {
    return BASIC_INFO_API_FIELDS[fieldId] ?? null
  }

  // Dynamic body-information resolution.
  // The ref category combines the body type (+ Subtype, when applicable) with the field id:
  //   Truck/Tractor — TRUCK_BODY_<USE>_<FIELD_ID> or TRUCK_BODY_<USE>_<SUBTYPE>_<FIELD_ID>
  //     e.g. use='Ambulance', field='make' → 'TRUCK_BODY_AMBULANCE_MAKE'
  //   Trailer       — TRAILER_BODY_<TYPE>_<FIELD_ID> or TRAILER_BODY_<TYPE>_<SUBTYPE>_<FIELD_ID>
  //     e.g. type='Flatbed', field='length' → 'TRAILER_BODY_FLATBED_LENGTH'
  // Returns null when the body type has not been selected yet.
  if (sectionId === 'body-information' && vehicleType === 'Truck/Tractor') {
    if (!isValid(deps.bodyUse)) return null
    const subtypeSuffix = isValid(deps.bodyUseSubtype) ? `_${normaliseToKey(deps.bodyUseSubtype!)}` : ''
    return `TRUCK_BODY_${normaliseToKey(deps.bodyUse!)}${subtypeSuffix}_${normaliseToKey(fieldId)}`
  }
  if (sectionId === 'body-information' && vehicleType === 'Trailer') {
    if (!isValid(deps.bodyType)) return null
    const subtypeSuffix = isValid(deps.bodyTypeSubtype) ? `_${normaliseToKey(deps.bodyTypeSubtype!)}` : ''
    return `TRAILER_BODY_${normaliseToKey(deps.bodyType!)}${subtypeSuffix}_${normaliseToKey(fieldId)}`
  }

  const map = SECTION_CATEGORY_MAPS[sectionId]
  if (!map) return null

  const resolvers =
    vehicleType === 'Truck/Tractor'
      ? { ...(map.truck ?? {}), ...(map.shared ?? {}) }
      : vehicleType === 'Trailer'
      ? { ...(map.trailer ?? {}), ...(map.shared ?? {}) }
      : map.shared ?? {}

  const resolver = resolvers[fieldId]
  return resolver ? resolver(deps) : null
}
